<template>
    <div class="cap-body">
        <DxDataGrid :data-source="ds" ref="gridRef" key-expr="Id" :show-borders="true" height="100%"
            :row-alternation-enabled="true" :hover-state-enabled="true"
            no-data-text="Nu există repartizări pentru angajatul selectat." @context-menu-preparing="onContextMenu">
            <DxScrolling mode="virtual" />
            <DxEditing :allow-updating="true" :allow-deleting="true" :allow-adding="true" mode="popup">
                <DxPopup :show-title="true" title="Repartizare centru de cost" :width="560" :height="400" />
                <DxFormPopup :col-count="1">
                    <DxItem data-field="Data" />
                    <DxItem data-field="CapitolId" />
                    <DxItem data-field="Ore" />
                    <DxItem data-field="Suma" />
                    <DxItem data-field="ContId" />
                </DxFormPopup>
            </DxEditing>

            <DxColumn data-field="Data" caption="Data" data-type="date" format="dd.MM.yyyy" :width="120" />
            <DxColumn data-field="CapitolId" caption="Centru de cost" :min-width="200">
                <DxLookup :data-source="dsCapitole" value-expr="Id" :display-expr="capDisplay" :search-enabled="true" />
            </DxColumn>
            <DxColumn data-field="Ore" caption="Ore" data-type="number" :width="80" alignment="center" />
            <DxColumn data-field="Suma" caption="Sumă" data-type="number" format="#,##0.00" :width="110" />
            <DxColumn data-field="ContId" caption="Cont contabil" :min-width="180">
                <DxLookup :data-source="dsConturi" value-expr="Id" :display-expr="contDisplay" :search-enabled="true" />
            </DxColumn>
        </DxDataGrid>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref } from 'vue';
import {
    DxDataGrid, DxColumn, DxScrolling, DxEditing, DxLookup, DxPopup, DxForm as DxFormPopup, DxItem
} from 'devextreme-vue/data-grid';
import ODataStore from 'devextreme/data/odata/store';
import DataSource from 'devextreme/data/data_source';
import AppSetting from '@/settings/appSettings';
import notify from 'devextreme/ui/notify';
import RU_Pontaj from '@/services/RU/RU_Pontaj';

function odata(entity: string, searchExpr: string[]) {
    return {
        store: new ODataStore({
            url: AppSetting.API_Url_ResurseUmaneOdata + entity, key: 'Id',
            beforeSend: (e: any) => { e.headers = { Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt') }; }
        }), paginate: true, pageSize: 50, searchExpr
    } as any;
}

export default defineComponent({
    components: { DxDataGrid, DxColumn, DxScrolling, DxEditing, DxLookup, DxPopup, DxFormPopup, DxItem },
    props: {
        empId: { type: String, default: null },
        lunaSel: { type: Number, default: null },
        anSel: { type: Number, default: null },
    },
    emits: ['refresh'],
    data() {
        return {
            gridRef: ref(null),
            ds: {} as any,
            dsCapitole: odata('DOC_Capitole', ['Nume', 'Cod']),
            dsConturi: odata('CON_Conturi', ['Simbol', 'Nume']),
        };
    },
    computed: {
        dataLuna(): string | null {
            if (!this.lunaSel || !this.anSel) return null;
            return `${this.anSel}-${String(this.lunaSel).padStart(2, '0')}-01`;
        },
    },
    watch: { empId() { this.build(); } },
    methods: {
        capDisplay(i: any) { return i ? [i.Cod, i.Nume].filter(Boolean).join(' - ') : ''; },
        contDisplay(i: any) { return i ? [i.Simbol, i.Nume].filter(Boolean).join(' - ') : ''; },
        build() {
            this.ds = new DataSource({
                store: new ODataStore({
                    version: 4, url: AppSetting.API_Url_ResurseUmaneOdata + 'SAL_CapitoleRepartizare', key: 'Id',
                    keyType: { Id: 'Guid' }, fieldTypes: { Id: 'Guid', AngajatId: 'Guid', CapitolId: 'Guid', ContId: 'Guid' },
                    beforeSend: (e: any) => { e.headers = { Authorization: 'Bearer ' + localStorage.getItem('ERA_Jwt') }; },
                    onInserting: (v: any) => { v.AngajatId = this.empId; },
                }),
                filter: ['AngajatId', '=', this.empId || '00000000-0000-0000-0000-000000000000'],
            });
        },
        onContextMenu(e: any) {
            if (e.target !== 'content' && e.row?.rowType !== 'data') return;
            e.items = e.items || [];
            e.items.push(
                { text: 'Repartizare — toți', onItemClick: () => this.repartizeaza() },
                { text: 'Repartizare — angajat curent', disabled: !this.empId, onItemClick: () => this.repartizeaza(this.empId) },
                { text: 'Ștergere — toți', onItemClick: () => this.sterge() },
                { text: 'Ștergere — angajat curent', disabled: !this.empId, onItemClick: () => this.sterge(this.empId) },
            );
        },
        async repartizeaza(angajatId?: string) {
            if (!this.dataLuna) return;
            try {
                await RU_Pontaj.repartizeazaCentreCost(this.dataLuna, angajatId);
                notify('Repartizarea centrelor de cost a fost realizată.', 'success', 2500);
                this.build();
                this.$emit('refresh');
            } catch (e) { console.error(e); notify('Eroare la repartizarea centrelor de cost!', 'error', 3000); }
        },
        async sterge(angajatId?: string) {
            if (!this.dataLuna) return;
            try {
                await RU_Pontaj.stergeRepartizari(this.dataLuna, angajatId);
                notify('Repartizările au fost șterse.', 'success', 2500);
                this.build();
                this.$emit('refresh');
            } catch (e) { console.error(e); notify('Eroare la ștergerea repartizărilor!', 'error', 3000); }
        },
    },
    mounted() { this.build(); },
});
</script>
<style scoped>.cap-body { height: 100%; }</style>
